import { useState, useEffect } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Input } from '@/shared/components/ui/Input';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { useToast } from '@/shared/components/ui/Toast';
import { useNotificationSettings } from '../hooks/useAdmin';
import {
  useUserNotificationPreferences,
  useUpdateUserPhoneNumber,
  useUpsertUserNotificationPreference,
  useUserSmsOptInStatus,
  useSetUserSmsOptIn,
} from '@/features/settings/hooks/useNotificationPreferences';
import type { UserWithLocations } from '../services/usersService';
import type { NotificationType } from '@/shared/types/domain.types';

interface UserNotificationPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserWithLocations | undefined;
}

const NOTIFICATION_TYPES: { type: NotificationType; label: string }[] = [
  { type: 'FRAMES_PENDING', label: 'Frames Pending' },
  { type: 'FRAMES_ORDERED_NOT_RECEIVED', label: 'Frames Ordered Not Received' },
  { type: 'LOCATION_INACTIVE', label: 'Location Inactive' },
];

export function UserNotificationPreferencesModal({
  isOpen,
  onClose,
  user,
}: UserNotificationPreferencesModalProps) {
  const { showToast } = useToast();
  
  // Queries
  const { data: globalSettings } = useNotificationSettings();
  const { data: preferences, isLoading } = useUserNotificationPreferences(user?.id || '');
  const { data: smsOptedIn } = useUserSmsOptInStatus(user?.id || '');
  
  // Mutations
  const updatePhoneMutation = useUpdateUserPhoneNumber();
  const upsertPrefMutation = useUpsertUserNotificationPreference();
  const setSmsOptInMutation = useSetUserSmsOptIn();
  
  // Local state
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Initialize phone input when user changes
  useEffect(() => {
    if (user) {
      setPhoneInput(user.phone_number || '');
      setPhoneError('');
    }
  }, [user]);

  if (!user) return null;

  // Get global threshold for a notification type
  const getGlobalThreshold = (type: NotificationType): number => {
    const setting = globalSettings?.find(s => s.notification_type === type);
    return setting?.threshold_days ?? 7;
  };

  // Get user preference for a notification type
  const getPreference = (type: NotificationType) => {
    return preferences?.find(p => p.notification_type === type && p.location_id === null);
  };

  // Format phone number
  const handlePhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, '');
    
    if (digits.length === 0) {
      setPhoneInput('');
      setPhoneError('');
    } else if (digits.length <= 11) {
      const formatted = digits.startsWith('1') 
        ? `+${digits}` 
        : `+1${digits}`;
      setPhoneInput(formatted);
      
      if (formatted.length === 12) {
        setPhoneError('');
      } else {
        setPhoneError('Phone number must be 10 digits');
      }
    }
  };

  // Save phone number
  const handleSavePhone = async () => {
    const phoneToSave = phoneInput.trim() || null;
    
    if (phoneToSave && !/^\+1[0-9]{10}$/.test(phoneToSave)) {
      setPhoneError('Phone number must be in format +1XXXXXXXXXX');
      return;
    }

    try {
      await updatePhoneMutation.mutateAsync({ userId: user.id, phone_number: phoneToSave });
      showToast('Phone number updated', 'success');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to update phone number',
        'error'
      );
    }
  };

  // Update preference
  const handlePreferenceChange = async (
    type: NotificationType,
    updates: { wants_email?: boolean; wants_sms?: boolean; threshold_days_override?: number | null }
  ) => {
    const existing = getPreference(type);
    
    try {
      await upsertPrefMutation.mutateAsync({
        userId: user.id,
        input: {
          notification_type: type,
          location_id: null,
          wants_email: updates.wants_email ?? existing?.wants_email ?? true,
          wants_sms: updates.wants_sms ?? existing?.wants_sms ?? false,
          threshold_days_override: updates.threshold_days_override !== undefined 
            ? updates.threshold_days_override 
            : existing?.threshold_days_override ?? null,
        },
      });
      showToast('Preference updated', 'success');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to update preference',
        'error'
      );
    }
  };

  const hasPhoneNumber = (!!phoneInput && phoneInput.length === 12) || !!user.phone_number;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Notification Preferences: ${user.name}`}
      size="lg"
    >
      <div className="space-y-6">
        {/* User Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm">
            <p><span className="font-medium">Email:</span> {user.email}</p>
            <p className="mt-1">
              <span className="font-medium">Locations:</span>{' '}
              {user.locations.length > 0 
                ? user.locations.map(l => l.short_code).join(', ')
                : 'None assigned'
              }
            </p>
          </div>
        </div>

        {/* Phone Number */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-2">Phone Number</h3>
          <div className="flex items-start gap-3">
            <div className="flex-1 max-w-xs">
              <Input
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={phoneInput}
                onChange={(e) => handlePhoneChange(e.target.value)}
                error={phoneError}
              />
            </div>
            <Button
              size="sm"
              onClick={handleSavePhone}
              disabled={updatePhoneMutation.isPending || !!phoneError || phoneInput === (user.phone_number || '')}
            >
              Save
            </Button>
          </div>

          {/* SMS Consent Checkbox - only show when user has phone number */}
          {hasPhoneNumber && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={smsOptedIn ?? false}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setSmsOptInMutation.mutate(
                      { userId: user.id, optedIn: newValue },
                      {
                        onSuccess: () => {
                          showToast(
                            newValue 
                              ? 'SMS opt-in consent saved' 
                              : 'SMS opt-in consent removed',
                            'success'
                          );
                        },
                        onError: (error) => {
                          showToast(
                            error instanceof Error ? error.message : 'Failed to update SMS opt-in',
                            'error'
                          );
                        },
                      }
                    );
                  }}
                  disabled={setSmsOptInMutation.isPending}
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <div className="flex-1">
                  <span className="text-sm text-gray-900">
                    I agree to receive SMS text messages from Optical Management System for operational alerts, reminders, and account notifications. Message frequency varies. Reply STOP to opt out.
                  </span>
                </div>
              </label>
            </div>
          )}
        </div>

        {/* Preferences Table */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">Notification Preferences</h3>
          
          {isLoading ? (
            <div className="animate-pulse space-y-2">
              <div className="h-12 bg-gray-100 rounded"></div>
              <div className="h-12 bg-gray-100 rounded"></div>
              <div className="h-12 bg-gray-100 rounded"></div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 font-medium text-gray-700">Type</th>
                  <th className="text-center py-2 font-medium text-gray-700">Email</th>
                  <th className="text-center py-2 font-medium text-gray-700">SMS</th>
                  <th className="text-center py-2 font-medium text-gray-700">Threshold</th>
                </tr>
              </thead>
              <tbody>
                {NOTIFICATION_TYPES.map((notifType) => {
                  const pref = getPreference(notifType.type);
                  const globalThreshold = getGlobalThreshold(notifType.type);
                  const effectiveThreshold = pref?.threshold_days_override ?? globalThreshold;
                  const isCustomThreshold = pref?.threshold_days_override !== null && pref?.threshold_days_override !== undefined;

                  return (
                    <tr key={notifType.type} className="border-b border-gray-100">
                      <td className="py-3">{notifType.label}</td>
                      <td className="py-3 text-center">
                        <input
                          type="checkbox"
                          checked={pref?.wants_email ?? true}
                          onChange={(e) => handlePreferenceChange(notifType.type, { wants_email: e.target.checked })}
                          disabled={upsertPrefMutation.isPending}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="py-3 text-center">
                        <input
                          type="checkbox"
                          checked={pref?.wants_sms ?? false}
                          onChange={(e) => handlePreferenceChange(notifType.type, { wants_sms: e.target.checked })}
                          disabled={upsertPrefMutation.isPending || !hasPhoneNumber || !smsOptedIn}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                          title={!hasPhoneNumber ? 'Add phone number to enable SMS' : !smsOptedIn ? 'User must opt in to SMS first' : undefined}
                        />
                      </td>
                      <td className="py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="number"
                            min="1"
                            max="365"
                            value={effectiveThreshold}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              if (!isNaN(val) && val > 0) {
                                handlePreferenceChange(notifType.type, { 
                                  threshold_days_override: val === globalThreshold ? null : val 
                                });
                              }
                            }}
                            disabled={upsertPrefMutation.isPending}
                            className="w-14 px-2 py-1 text-center text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                          />
                          <span className="text-gray-500">days</span>
                          {isCustomThreshold && (
                            <Badge variant="info" className="ml-1 text-xs">custom</Badge>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Legend */}
        <div className="text-xs text-gray-500 bg-gray-50 rounded p-3">
          <p><strong>Note:</strong> Global default thresholds are set in the Notifications tab. 
          Users can override these thresholds. A "custom" badge indicates a user override.</p>
        </div>

        {/* Close Button */}
        <div className="flex justify-end pt-2 border-t">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

